import { ApiError, UnreadableResponseError } from './http.ts';

/**
 * Error codes the API raises when something it depends on failed, rather than the caller or the
 * API itself. Each maps to a 502 carrying a `serverMessage` written for an administrator.
 *
 * <b>This list is the client half of a contract and has to be kept in step with the server's
 * `UpstreamException` call sites.</b> A code missing from here is not a crash — it degrades to
 * the generic 5xx sentence, which is exactly what an unknown upstream failure deserves — so the
 * failure mode of forgetting one is a lost explanation and never a broken screen.
 */
const UPSTREAM_CODES = new Set([
  'directory_unavailable',
  'directory_throttled',
  'directory_forbidden',
  'directory_not_configured',
]);

/**
 * Turns a failed request into something worth showing a user.
 *
 * Returns null when there is no error, so callers can render unconditionally. The distinction
 * that matters most is 403 versus everything else: "you're not allowed to do that" is a
 * different problem from "that didn't work", and conflating them sends people to look in the
 * wrong place.
 *
 * <b>The one rule underneath all of it: never assert a cause this cannot know.</b> The connection
 * line below is the last resort and it is a claim about the network, so anything that did reach the
 * server has to be caught before it — see `UnreadableResponseError`, which exists because a
 * successful 200 was being reported as a connection failure for months.
 */
export function describeApiError(error: unknown): string | null {
  if (!error) return null;

  /*
   * BEFORE THE STATUS BRANCHES, and it has to be: this is a subclass of ApiError carrying the real
   * status, so a 200 that would not parse falls through to "That request was rejected" — which is
   * wrong twice over, since nothing was rejected and there is nothing in the request to check.
   *
   * The status is named because it is the surprising half and it is what tells a reader where to
   * look. A 200 here means the app and the API disagree about a body; a 502 means something in
   * between answered with a page.
   */
  if (error instanceof UnreadableResponseError) {
    return (
      `The server answered (${error.status}) but its response could not be read. ` +
      'That is a problem with this app or the API rather than with your connection — ' +
      'try again, and report it if it persists.'
    );
  }

  if (!(error instanceof ApiError)) {
    // Genuinely unreached-the-server territory: `fetch` rejecting, an aborted request, a thrown
    // non-Error. This is the only branch entitled to mention the connection.
    return 'Something went wrong. Check your connection and try again.';
  }

  if (error.status === 401) {
    return 'Your session has expired. Sign in again to continue.';
  }

  if (error.status === 403) {
    // The API explains room entitlement precisely; fall back to a generic line.
    return error.serverMessage ?? "You don't have permission to do that.";
  }

  if (error.status === 404) {
    return error.serverMessage ?? 'That no longer exists — it may have been removed.';
  }

  /*
   * An upstream dependency failed and the API said which one and why — a Graph throttle, a
   * missing consent, a timeout. The reason is the deliverable: "wait a moment" and "somebody
   * must grant a permission" send an administrator to completely different places, and the
   * generic 5xx line below sends them to neither.
   *
   * <b>Branched on the CODE, never on the status, and that is the load-bearing part.</b> App
   * Service's own front end answers 502 with an HTML body when the container is unhealthy;
   * `readErrorBody` cannot parse that, so such an error reaches here with no code and no
   * serverMessage at all. A `status === 502` rule would put this app's voice on a platform
   * failure it knows nothing about — and would have nothing to say, since there is no message to
   * quote. The control test for exactly that case sits beside the claim in `errors.test.js`.
   */
  if (error.code !== undefined && UPSTREAM_CODES.has(error.code) && error.serverMessage) {
    return error.serverMessage;
  }

  if (error.status >= 500) {
    return 'The server had a problem. Try again in a moment.';
  }

  return error.serverMessage ?? 'That request was rejected. Check the details and try again.';
}
